import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowUpRight, Clock } from 'lucide-react';
import { Reveal, PageHero } from '../components/Shared';
import { publicApi, mediaUrl, formatPostDate } from '../lib/publicApi';
import { useSeo } from '../hooks/useSeo';

const CATEGORIES = ['AI & Automation', 'Software & Digital Products', 'Enterprise Technology', 'Digital Growth', 'Company News'];

const PostCard = ({ post, featured = false }) => (
  <Link to={`/blog/${post.slug}`} className={`post-card ${featured ? 'post-card-featured' : ''}`} data-testid={`post-card-${post.slug}`}>
    <div className="post-card-cover">
      {post.cover_image ? <img src={mediaUrl(post.cover_image)} alt={post.title} loading="lazy" /> : <span className="post-card-cover-empty" />}
      <span className="post-card-tag">{post.category}</span>
    </div>
    <div className="post-card-body">
      <h3>{post.title}</h3>
      <p className="post-card-excerpt">{post.excerpt}</p>
      <div className="post-card-meta">
        <span>{post.author?.name || 'TechSpitze'}</span>
        <span>{formatPostDate(post.publish_date)}</span>
        <span><Clock size={12} />{post.read_time || '3 min read'}</span>
      </div>
      <span className="post-card-more">Read article <ArrowUpRight size={15} /></span>
    </div>
  </Link>
);

const Skeleton = () => <div className="post-skeleton" aria-hidden="true" />;

export default function Blog() {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: 'Insights — TechSpitze',
    description: 'Practical thinking on AI automation, enterprise technology, digital products and growth from the TechSpitze team.',
  });

  useEffect(() => { const t = setTimeout(() => { setDebounced(search); setPage(1); }, 320); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [category]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    publicApi.posts(new URLSearchParams({ search: debounced, category, page, limit: 9 }).toString())
      .then(data => { if (!active) return; setItems(prev => (page === 1 ? data.items : [...prev, ...data.items])); setTotal(data.total); })
      .catch(() => { if (active) setItems([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [debounced, category, page]);

  const showFeatured = page >= 1 && !debounced && !category && items.length > 0;
  const featured = showFeatured ? items[0] : null;
  const rest = useMemo(() => (showFeatured ? items.slice(1) : items), [items, showFeatured]);

  return <main id="main-content" className="blog-page" data-testid="blog-page">
    <PageHero id="blog-hero" eyebrow="THE TECHSPITZE JOURNAL" lines={[{ text: 'Insights' }]} description="Field notes on AI, automation and enterprise technology — written by the people doing the work." />

    <section className="container blog-controls">
      <Reveal className="blog-search">
        <Search size={16} />
        <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search insights" aria-label="Search insights" data-testid="blog-search" />
      </Reveal>
      <Reveal className="blog-chips" delay={.08}>
        <button type="button" className={`blog-chip ${!category ? 'is-on' : ''}`} onClick={() => setCategory('')} data-testid="blog-chip-all">All</button>
        {CATEGORIES.map(item => <button type="button" key={item} className={`blog-chip ${category === item ? 'is-on' : ''}`} onClick={() => setCategory(item)} data-testid={`blog-chip-${item.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{item}</button>)}
      </Reveal>
    </section>

    <section className="container blog-listing">
      {loading && page === 1
        ? <div className="post-grid" data-testid="blog-loading">{[...Array(6)].map((_, i) => <Skeleton key={i} />)}</div>
        : items.length === 0
          ? <div className="content-empty" data-testid="blog-empty"><h3>Nothing here yet</h3><p>{debounced || category ? 'No insights match that search yet — try another term or category.' : 'The first insights are on their way.'}</p></div>
          : <>
            {featured && <Reveal data-testid="blog-featured"><PostCard post={featured} featured /></Reveal>}
            <div className="post-grid">{rest.map(post => <Reveal key={post.slug}><PostCard post={post} /></Reveal>)}</div>
            {items.length < total && <div className="blog-load-more">
              <button type="button" className="action-button button-secondary" onClick={() => setPage(p => p + 1)} disabled={loading} data-testid="blog-load-more">{loading ? 'Loading…' : 'Load more'}</button>
            </div>}
          </>}
    </section>
  </main>;
}
